import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { formatMoney } from '@/shared/utils/money';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { listAdminMyFatoorahTransactions } from '../services/adminMyfatoorahTransactions.api';
import type {
  AdminMyFatoorahTransactionItem,
  ListAdminMyFatoorahTransactionsQuery,
  MyFatoorahTransactionStatus,
} from '../types/adminMyfatoorahTransaction';
import { MYFATOORAH_TRANSACTION_STATUS_OPTIONS } from '../types/adminMyfatoorahTransaction';
import { computeMyFatoorahTransactionKpis } from '../utils/myfatoorahTransactionHelpers';
import { formatDate } from '../utils/paymentHelpers';
import { MyFatoorahTransactionStatusBadge } from '../components/MyFatoorahTransactionStatusBadge';
import { MyFatoorahTransactionDetailDrawer } from '../components/MyFatoorahTransactionDetailDrawer';

export function MyFatoorahTransactionsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [statusFilter, setStatusFilter] = useState<MyFatoorahTransactionStatus | ''>('');
  const [productFilter, setProductFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [user, isSuperAdmin, navigate]);

  const serverQuery = useMemo((): ListAdminMyFatoorahTransactionsQuery => {
    const q: ListAdminMyFatoorahTransactionsQuery = { limit: 100 };
    if (statusFilter) q.status = statusFilter;
    if (productFilter) q.product_id = productFilter;
    if (dateFrom) q.from = new Date(`${dateFrom}T00:00:00`).toISOString();
    if (dateTo) q.to = new Date(`${dateTo}T23:59:59.999`).toISOString();
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    return q;
  }, [statusFilter, productFilter, dateFrom, dateTo, debouncedSearch]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'payments', 'myfatoorah', 'transactions', 'list', serverQuery],
    queryFn: () => listAdminMyFatoorahTransactions(serverQuery),
    enabled: isSuperAdmin,
  });

  const { data: productsData } = useProducts({ limit: 100 });
  const products = useMemo(() => productsData?.items ?? [], [productsData?.items]);

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const kpis = useMemo(() => computeMyFatoorahTransactionKpis(items), [items]);

  const unavailable = t('payments.identity.unavailable');

  const columns: ColumnDef<AdminMyFatoorahTransactionItem>[] = useMemo(
    () => [
      {
        key: 'customer',
        header: t('payments.myfatoorah.columns.customer'),
        accessor: (row) => (
          <div className="space-y-0.5 min-w-0">
            <TrialTruncatedText
              value={row.customer_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="font-medium"
            />
            <TrialTruncatedText
              value={row.customer_email}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="text-xs text-muted-foreground"
            />
          </div>
        ),
      },
      {
        key: 'product',
        header: t('payments.myfatoorah.columns.product'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.product_name}
            fallback={unavailable}
            maxWidthClass="max-w-[10rem]"
          />
        ),
        sortValue: (row) => row.product_name,
      },
      {
        key: 'plan',
        header: t('payments.myfatoorah.columns.plan'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.plan_name}
            fallback={unavailable}
            maxWidthClass="max-w-[10rem]"
          />
        ),
        sortValue: (row) => row.plan_name,
      },
      {
        key: 'amount',
        header: t('payments.myfatoorah.columns.amount'),
        accessor: (row) => (
          <span className="font-mono tabular-nums whitespace-nowrap">
            {formatMoney(row.amount, row.currency)}
          </span>
        ),
        align: 'right',
        sortValue: (row) => row.amount,
      },
      {
        key: 'currency',
        header: t('payments.myfatoorah.columns.currency'),
        accessor: (row) => <span className="text-xs font-mono">{row.currency}</span>,
        align: 'center',
      },
      {
        key: 'invoiceId',
        header: t('payments.myfatoorah.columns.invoiceId'),
        accessor: (row) => (
          <TrialTruncatedText
            value={row.provider_invoice_id}
            fallback={unavailable}
            maxWidthClass="max-w-[10rem]"
            nowrap
            className="font-mono text-xs"
          />
        ),
      },
      {
        key: 'status',
        header: t('payments.myfatoorah.columns.status'),
        accessor: (row) => <MyFatoorahTransactionStatusBadge status={row.status} />,
        align: 'center',
        sortValue: (row) => row.status,
      },
      {
        key: 'created',
        header: t('payments.myfatoorah.columns.created'),
        accessor: (row) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(row.created_at, locale)}
          </span>
        ),
        sortValue: (row) => row.created_at,
      },
      {
        key: 'actions',
        header: t('payments.myfatoorah.columns.actions'),
        accessor: (row) => (
          <div className="flex justify-end whitespace-nowrap shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPaymentId(row.payment_id)}
            >
              <Icons.Eye className="h-3.5 w-3.5" />
              {t('common.view')}
            </Button>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable],
  );

  if (!user || !isSuperAdmin) return null;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('payments.myfatoorah.title')}
          subtitle={t('payments.myfatoorah.description')}
          eyebrow={t('payments.myfatoorah.eyebrow')}
          icon={<Icons.DollarSign className="h-7 w-7" />}
        />
        <KpiGrid className="gap-4 xl:grid-cols-3 2xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-5 animate-pulse">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="mt-3 h-8 w-16 rounded bg-muted" />
            </div>
          ))}
        </KpiGrid>
        <LoadingPanel />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('payments.myfatoorah.title')}
          subtitle={t('payments.myfatoorah.description')}
          eyebrow={t('payments.myfatoorah.eyebrow')}
          icon={<Icons.DollarSign className="h-7 w-7" />}
        />
        <ErrorPanel message={t('payments.myfatoorah.error.loadFailed')} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('payments.myfatoorah.title')}
        subtitle={t('payments.myfatoorah.description')}
        eyebrow={t('payments.myfatoorah.eyebrow')}
        icon={<Icons.DollarSign className="h-7 w-7" />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/dashboard/payments"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.myfatoorah.backToPayments')}
            </Link>
            <Link
              to="/dashboard/payments/instapay"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.instapay.title')}
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <Icons.RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      <KpiGrid className="gap-4 xl:grid-cols-3 2xl:grid-cols-5">
        <KpiCard
          title={t('payments.myfatoorah.kpi.total')}
          value={String(kpis.total)}
          tone="blue"
        />
        <KpiCard title={t('payments.myfatoorah.kpi.paid')} value={String(kpis.paid)} tone="green" />
        <KpiCard
          title={t('payments.myfatoorah.kpi.pending')}
          value={String(kpis.pending)}
          tone="amber"
        />
        <KpiCard
          title={t('payments.myfatoorah.kpi.failed')}
          value={String(kpis.failed)}
          tone="rose"
        />
        <KpiCard
          title={t('payments.myfatoorah.kpi.cancelled')}
          value={String(kpis.cancelled)}
          tone="violet"
        />
      </KpiGrid>

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5 md:col-span-2">
            <label htmlFor="mf-search" className="text-xs font-medium text-muted-foreground">
              {t('payments.filters.search')}
            </label>
            <div className="flex gap-2">
              <Input
                id="mf-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('payments.myfatoorah.filters.searchPlaceholder')}
                onKeyDown={(e) => e.key === 'Enter' && setDebouncedSearch(searchQuery)}
              />
              <Button variant="secondary" onClick={() => setDebouncedSearch(searchQuery)}>
                {t('payments.filters.searchApply')}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="mf-status" className="text-xs font-medium text-muted-foreground">
              {t('payments.myfatoorah.filters.status')}
            </label>
            <select
              id="mf-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as MyFatoorahTransactionStatus | '')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.myfatoorah.filters.allStatuses')}</option>
              {MYFATOORAH_TRANSACTION_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`payments.myfatoorah.status.${s}`, s)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="mf-product" className="text-xs font-medium text-muted-foreground">
              {t('payments.myfatoorah.filters.product')}
            </label>
            <select
              id="mf-product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.myfatoorah.filters.allProducts')}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {i18n.language === 'ar' ? product.name_ar || product.name_en : product.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="mf-from" className="text-xs font-medium text-muted-foreground">
              {t('payments.myfatoorah.filters.dateFrom')}
            </label>
            <Input
              id="mf-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="mf-to" className="text-xs font-medium text-muted-foreground">
              {t('payments.myfatoorah.filters.dateTo')}
            </label>
            <Input
              id="mf-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t('payments.myfatoorah.filters.hint')}</p>
      </div>

      {items.length === 0 ? (
        <EmptyPanel
          title={t('payments.myfatoorah.empty.title')}
          message={t('payments.myfatoorah.empty.message')}
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={items}
            rowKey={(row) => row.payment_id}
            total={data?.total ?? items.length}
          />
          <p className="text-xs text-muted-foreground">
            {t('payments.footer.showing', {
              shown: items.length,
              total: data?.total ?? items.length,
            })}
          </p>
        </>
      )}

      <MyFatoorahTransactionDetailDrawer
        paymentId={selectedPaymentId}
        locale={locale}
        onClose={() => setSelectedPaymentId(null)}
      />
    </div>
  );
}
